import { useCallback, useRef, useMemo } from 'react'

/**
 * Hook to scroll the active tab into view
 *
 * @param {Array} tabs - Array of tab objects with { id, to, to1?, to2? } structure
 * @param {string} currentPath - Current pathname to find the active tab
 * @param {Object} scrollConfig - Optional scroll behavior config
 * @returns {Object} - { containerRef, tabRefs, scrollToActiveTab }
 *
 * @example
 * const { containerRef, tabRefs, scrollToActiveTab } = useScrollActiveTabIntoView(
 *   [{ id: 'tab1', to: '/matches' }],
 *   '/matches'
 * );
 */
const useScrollActiveTabIntoView = (
  tabs = [],
  currentPath,
  scrollConfig = {}
) => {
  const containerRef = useRef(null)
  const tabRefs = useRef({})

  // Memoize scroll options to prevent unnecessary re-renders
  const scrollOptions = useMemo(
    () => ({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
      ...scrollConfig,
    }),
    [scrollConfig]
  )

  // Create a Map for O(1) path-to-tab lookup (optimized for many tabs)
  const pathToTabMap = useMemo(() => {
    const map = new Map()

    tabs.forEach(tab => {
      // Add all possible paths for this tab
      if (tab.to) map.set(tab.to, tab)
      if (tab.to1) map.set(tab.to1, tab)
      if (tab.to2) map.set(tab.to2, tab)
    })

    return map
  }, [tabs])

  // Optimized scroll function with O(1) lookup
  const scrollToActiveTab = useCallback(() => {
    if (!currentPath || !pathToTabMap.size) return

    const activeTab = pathToTabMap.get(currentPath)

    if (activeTab?.id && tabRefs.current[activeTab.id]) {
      try {
        tabRefs.current[activeTab.id].scrollIntoView(scrollOptions)
      } catch (error) {
        console.warn('Failed to scroll tab into view:', error)
      }
    }
  }, [currentPath, pathToTabMap, scrollOptions])

  return {
    containerRef,
    tabRefs,
    scrollToActiveTab,
  }
}

export default useScrollActiveTabIntoView
